import { toBnDigits, formatBnDate, jobLogoSrc } from '../../../utils/bnFormat';
import usePosterFit from './usePosterFit';

/**
 * Shared pieces of the poster DESIGNS (templates/designs/*). A design is a layout of its own — its own structure, type and ornaments —
 * that takes colours from its palette; PosterBase (the original layout) is not touched.
 *
 * Every size is written in poster-width units (`cqw`, the sheet is a container) and the ones that scale with the content go through
 * fs(): the auto-fit hook (usePosterFit) tunes `--fs` until the content fills the A4 sheet exactly, so long text shrinks and short
 * text grows. A global `span{font-size:12px}` rule exists in the app — text is therefore put in divs, and any span carries its own size.
 */

/**
 * A flat colour as an OPAQUE IMAGE layer. The export (utils/posterExport capturePages) forces a white background colour onto the node it
 * captures, which replaces a plain `background-color` — so a poster whose base is only a colour (or a colour under translucent layers)
 * would come out on white. An image layer is never replaced.
 */
export const solid = (color) => `linear-gradient(${color}, ${color})`;

/** A size in poster-width units, multiplied by the auto-fit factor. */
export const fs = (n) => `calc(${n}cqw * var(--fs, 1))`;

/** #hex → rgba() (for translucent glows, tints and glass). */
export function hexToRgba(hex, a) {
    const h = String(hex || '#000').replace('#', '');
    const n = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const r = parseInt(n.slice(0, 2), 16) || 0;
    const g = parseInt(n.slice(2, 4), 16) || 0;
    const b = parseInt(n.slice(4, 6), 16) || 0;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/** Long values never get cut: the font shrinks (down to a floor) as the text grows. `comfy` = the length that still renders full size. */
export function valueFitScale(text, comfy = 18) {
    const len = String(text || '').length;
    if (len <= comfy) return 1;
    return Math.max(0.6, Math.sqrt(comfy / len));
}

/** A big number's size factor by its length: "৫০" full size, "৫০০+" / "প্রায় ১০০" smaller so it stays inside its badge. */
export function numberScale(text) {
    const len = String(text || '').length;
    if (len <= 2) return 1;
    if (len === 3) return 0.84;
    if (len === 4) return 0.68;
    return Math.max(0.42, 0.56 - (len - 5) * 0.04);
}

/** A wide footer puts the shop on ONE line; when the name + address are long that squeezes the address into a sliver, so it wraps to two rows. */
export const shopIsLong = (shop) => String(shop.nameShown || '').length + String(shop.addressShown || '').length > 56;

/** "৭ অক্টোবর" → { day: '৭', month: 'অক্টোবর' } — for designs that draw a date as a calendar tile. */
export function dateParts(iso) {
    const text = formatBnDate(iso);
    const [day, ...rest] = text.split(' ');
    return { day: day || '', month: rest.join(' ') };
}

/** The poster's qualification / age text — or "প্রযোজ্য নয়" ("not applicable") when the job has none (empty, or only spaces). */
export const posterTextOrNA = (value, t) => String(value ?? '').trim() || t('biggapon.notApplicable');

/** What every design draws, read once from the notice + the shop + the translations. */
export function posterFields(job, business, t) {
    const shopName = business?.shopName?.trim();
    const address = business?.businessAddress?.trim();
    return {
        org: job.organization,
        position: job.position,
        edu: posterTextOrNA(job.posterQualification, t),
        age: posterTextOrNA(job.posterBosoyShima, t),
        vacancy: toBnDigits(job.totalVacancies || 0),
        start: formatBnDate(job.publishedDate),
        end: formatBnDate(job.deadlineDate),
        startParts: dateParts(job.publishedDate),
        endParts: dateParts(job.deadlineDate),
        shop: {
            name: shopName,
            nameShown: shopName || t('biggapon.shopNamePlaceholder'),
            address,
            addressShown: address || t('biggapon.addressPlaceholder'),
            phone: business?.phone?.trim(),
            logo: business?.shopLogo?.trim(),
            placeholder: !shopName,
            addressPlaceholder: !address,
        },
        label: {
            notice: t('biggapon.noticeTitle'),
            start: t('biggapon.startLabel'),
            end: t('biggapon.endLabel'),
            edu: t('biggapon.eduLabel'),
            vacancy: t('biggapon.vacancyLabel'),
            unit: t('biggapon.vacancyUnit'),
            age: t('biggapon.ageLabel'),
            promo: t('biggapon.promoLine1'),
            cta: t('biggapon.contactCta'),
        },
    };
}

/** The notice's logo (or the default), contained inside a square of `size` cqw. Pass `style` for a ring / shadow around it. */
export function PosterLogo({ job, size = 14, scaled = true, className = '', style }) {
    const px = scaled ? fs(size) : `${size}cqw`;
    return (
        <div className={`flex flex-none items-center justify-center ${className}`} style={{ width: px, height: px, ...style }}>
            <img src={jobLogoSrc(job)} alt="" className="h-full w-full object-contain" />
        </div>
    );
}

/**
 * The fixed A4 sheet every design stands on (portrait 210:297, or landscape 297:210). `body` flows (and is what the auto-fit scales), `footer` sits under it, `decor` is painted
 * behind everything, `overlay` on top (frames, corner ornaments, stamps). The sheet has the page-wide `fontFamily`.
 */
export function PosterSheet({ deps, className = '', background, fontFamily, decor, body, bodyClassName = '', bodyStyle, footer, overlay, landscape = false }) {
    const { boxRef, innerRef, contentRef } = usePosterFit(deps);
    // a bare colour becomes an image layer (see solid): the export would otherwise paint it white
    const base = typeof background === 'string' && !/gradient\(|url\(/.test(background) ? solid(background) : background;
    return (
        <div
            ref={boxRef}
            className={`relative w-full overflow-hidden ${className}`}
            style={{ aspectRatio: landscape ? '297 / 210' : '210 / 297', containerType: 'inline-size', background: base, fontFamily, '--fs': 1 }}
        >
            {decor}
            <div ref={innerRef} className="relative flex min-h-full flex-col">
                <div ref={contentRef} className={`flex flex-1 flex-col ${bodyClassName}`} style={bodyStyle}>{body}</div>
                {footer}
            </div>
            {overlay}
        </div>
    );
}
